import {
    Component,
    DestroyRef,
    Input,
    OnInit,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UIRouterModule } from '@uirouter/angular';
import { ParamService } from '@monsido/core/param/param.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import { STATISTICS } from '@client/app/shared-modules/statistics/statistics.constant';
import {
    StatisticsDateRangeService,
} from '@monsido/modules/statistics/services/date-range/statistics-date-range.service';
import moment from 'moment';
import type { ChartData, ChartOptions } from 'chart.js';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';
import {
    SiteAnalyticsDateRangePickerComponent,
} from '@client/app/feature-modules/site-analytics/components/date-range-picker/date-range-picker.component';
import {
    VISITORS_SUMMARY_PAGE_CONFIG,
} from '@client/app/feature-modules/site-analytics/configs/visitors-summary-page.config';
import type {
    SiteAnalyticsSummaryReferrerCardConfig,
    SiteAnalyticsSummaryScaffoldConfig,
    SiteAnalyticsSummarySourceMetric,
    SiteAnalyticsSummarySourceSeriesKey,
    SiteAnalyticsSummaryVisitorsCardConfig,
} from '@client/app/feature-modules/site-analytics/types/site-analytics-summary-scaffold.type';
import type { StatisticsKeyword } from '@client/app/modules/statistics/interfaces/statistics-keyword.interface';
import type { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import type { StatisticsVisitor } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import type { RequestParamsType } from '@monsido/ng2/external/http/options';

interface SummaryListItem {
    label: string;
    visits: number;
    percentage: number;
}

interface VisitorsSummaryCardViewModel {
    id: SiteAnalyticsSummaryVisitorsCardConfig['id'];
    titleKey: string;
    overviewLinkLabelKey: string;
    overviewState: string;
    chartType: 'doughnut' | 'bubble';
    chartData: ChartData<'doughnut'> | ChartData<'bubble'>;
    chartOptions: ChartOptions<'doughnut'> | ChartOptions<'bubble'>;
    items: SummaryListItem[];
    hasData: boolean;
}

interface SourcesReferrerCardViewModel {
    id: SiteAnalyticsSummaryReferrerCardConfig['id'];
    titleKey: string;
    overviewLinkLabelKey: string;
    overviewState: string;
    items: SummaryListItem[];
}

const DEFAULT_DOUGHNUT_COLORS = ['#2C73D9', '#6EA6FF', '#9BC2FF'];
const DEFAULT_BUBBLE_COLORS = ['#2C73D9', '#6EA6FF', '#9BC2FF'];

@Component({
    selector: 'mon-statistics-summary-scaffold-page',
    standalone: true,
    templateUrl: './statistics-summary-scaffold-page.component.html',
    styleUrls: ['./statistics-summary-scaffold-page.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        TranslocoModule,
        UIRouterModule,
        CardModule,
        ChartModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        TableModule,
        PageLayoutComponent,
        SiteAnalyticsDateRangePickerComponent,
    ],
})
export class StatisticsSummaryScaffoldPageComponent implements OnInit {
    @Input() config: SiteAnalyticsSummaryScaffoldConfig = VISITORS_SUMMARY_PAGE_CONFIG;

    private readonly paramService = inject(ParamService);
    private readonly dateRangeService = inject(DateRangeService);
    private readonly statisticsDateRangeService = inject(StatisticsDateRangeService);
    private readonly statsRepoService = inject(StatsRepoService);
    private readonly translocoService = inject(TranslocoService);
    private readonly destroyRef = inject(DestroyRef);

    primaryDateRange: [Date | null, Date | null] | null = null;

    loading = false;
    visitorsCards: VisitorsSummaryCardViewModel[] = [];

    selectedSourceMetric: SiteAnalyticsSummarySourceMetric = 'visits';
    sourceChartType: 'bar' | 'line' = 'bar';
    sourceChartData: ChartData<'bar' | 'line'> = {
        labels: [],
        datasets: [],
    };
    sourceChartOptions: ChartOptions<'bar' | 'line'> = {};
    hasSourceChartData = false;

    sourcesSummary: StatisticsSummary | null = null;
    sourceReferrerCards: SourcesReferrerCardViewModel[] = [];
    sourceKeywords: StatisticsKeyword[] = [];

    private latestRequestId = 0;
    private sourceRemoteData: Record<SiteAnalyticsSummarySourceSeriesKey, StatisticsVisitor[]> = {
        organic: [],
        direct: [],
        referring: [],
        social: [],
        paid: [],
    };

    get sourceMetricOptions (): Array<{ value: SiteAnalyticsSummarySourceMetric; label: string }> {
        return (this.config.sourcesMetricOptions || []).map((option) => ({
            value: option.value,
            label: this.translocoService.translate(option.labelKey),
        }));
    }

    ngOnInit (): void {
        this.initializeSourceMetric();

        const initialParams = this.paramService.getParams() as Record<string, unknown>;
        this.applyDateParams(initialParams, true);
        this.loadData();

        const deregisterHook = this.paramService.subscribeParamsChangeOnCurrentState((updated) => {
            this.applyDateParams(updated as Record<string, unknown>, false);
            this.loadData();
        }) as (() => void);

        this.destroyRef.onDestroy(() => {
            deregisterHook();
        });
    }

    onPrimaryDateRangeChange (value: [Date | null, Date | null] | null): void {
        this.primaryDateRange = value;

        if (!this.hasCompleteDateRange(value)) {
            return;
        }

        this.rememberPrimaryDateRange();
        this.paramService.updateParams({
            from: this.toDateParam(value[0]),
            to: this.toDateParam(value[1]),
        });
    }

    onSourceMetricChange (metric: SiteAnalyticsSummarySourceMetric): void {
        if (this.selectedSourceMetric === metric) {
            return;
        }

        this.selectedSourceMetric = metric;
        this.buildSourceChartData();
    }

    formatNumber (value: unknown): string {
        const numberValue = this.toFloat(value);
        return Math.round(numberValue).toLocaleString();
    }

    formatDuration (value: unknown): string {
        if (typeof value === 'string' && value.includes(':')) {
            return value;
        }

        const totalSeconds = Math.max(0, Math.round(this.toFloat(value)));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    formatPercentage (value: unknown): string {
        if (typeof value === 'string' && value.includes('%')) {
            return value;
        }

        const numberValue = this.toFloat(value);
        return `${numberValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    }

    getSourceMetricValue (metric: SiteAnalyticsSummarySourceMetric): string {
        const summary = this.sourcesSummary;

        if (!summary) {
            return '-';
        }

        switch (metric) {
            case 'uniqueVisits':
                return this.formatNumber(summary.visitors);
            case 'pageViews':
                return this.formatNumber(summary.pageviews);
            case 'avgVisitTime':
                return this.formatDuration(summary.avg_time);
            case 'bounceRate':
                return this.formatPercentage(summary.bounce_rate);
            default:
                return this.formatNumber(summary.visits);
        }
    }

    private initializeSourceMetric (): void {
        const firstMetric = this.config.sourcesMetricOptions?.[0]?.value;
        if (firstMetric) {
            this.selectedSourceMetric = firstMetric;
        }
    }

    private applyDateParams (params: Record<string, unknown>, syncIfMissing: boolean): void {
        const parsedRange = this.parseDateRange(params.from, params.to);

        if (parsedRange) {
            this.primaryDateRange = parsedRange;
            this.rememberPrimaryDateRange();
            return;
        }

        const persistedRange = this.statisticsDateRangeService.getLastSelectedRange();
        const fallback = persistedRange ?? this.dateRangeService.setRangeDates();
        this.primaryDateRange = [fallback.startDate.toDate(), fallback.endDate.toDate()];

        if (!syncIfMissing) {
            return;
        }

        this.paramService.updateParams({
            from: this.toDateParam(fallback.startDate.toDate()),
            to: this.toDateParam(fallback.endDate.toDate()),
        });
    }

    private rememberPrimaryDateRange (): void {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return;
        }

        this.statisticsDateRangeService.setLastSelectedRange({
            startDate: moment(this.primaryDateRange[0]),
            endDate: moment(this.primaryDateRange[1]),
        });
    }

    private async loadData (): Promise<void> {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return;
        }

        const requestId = ++this.latestRequestId;
        this.loading = true;

        try {
            if (this.config.mode === 'sources') {
                await this.loadSourcesData(requestId);
            } else {
                await this.loadVisitorsData(requestId);
            }
        } finally {
            if (requestId === this.latestRequestId) {
                this.loading = false;
            }
        }
    }

    private async loadVisitorsData (requestId: number): Promise<void> {
        const cardConfigs = this.config.visitorsCards || [];
        const params = this.buildDateParams();

        const cards = await Promise.all(cardConfigs.map((cardConfig) => this.loadVisitorsCard(cardConfig, params)));

        if (requestId !== this.latestRequestId) {
            return;
        }

        this.visitorsCards = cards;
    }

    private async loadVisitorsCard (
        cardConfig: SiteAnalyticsSummaryVisitorsCardConfig,
        params: RequestParamsType,
    ): Promise<VisitorsSummaryCardViewModel> {
        const response = await this.fetchVisitorsCardData(cardConfig.id, params)
            .catch((): StatisticsVisitor[] => []);
        const normalizedRows = response
            .map((entry) => ({
                label: String(entry.label || entry.url || '-'),
                visits: this.toInteger(entry.visits),
            }))
            .filter((entry) => entry.visits > 0)
            .sort((first, second) => second.visits - first.visits);

        const chartRows = cardConfig.id === 'screenResolutions'
            ? normalizedRows.filter((entry) => entry.label.toLowerCase() !== 'unknown')
            : normalizedRows;
        const topRows = normalizedRows.slice(0, 3);
        const topChartRows = chartRows.slice(0, 3);
        const totalVisits = Math.max(0, normalizedRows.reduce((sum, row) => sum + row.visits, 0));
        const items = topRows.map((entry) => ({
            ...entry,
            percentage: totalVisits > 0 ? (entry.visits * 100) / totalVisits : 0,
        }));

        return {
            id: cardConfig.id,
            titleKey: cardConfig.titleKey,
            overviewLinkLabelKey: cardConfig.overviewLinkLabelKey,
            overviewState: cardConfig.overviewState,
            chartType: cardConfig.chartType,
            chartData: cardConfig.chartType === 'bubble'
                ? this.createBubbleChartData(topChartRows)
                : this.createDoughnutChartData(topRows),
            chartOptions: cardConfig.chartType === 'bubble'
                ? this.createBubbleChartOptions()
                : this.createDoughnutChartOptions(),
            items,
            hasData: topRows.length > 0,
        };
    }

    private async fetchVisitorsCardData (
        cardId: SiteAnalyticsSummaryVisitorsCardConfig['id'],
        params: RequestParamsType,
    ): Promise<StatisticsVisitor[]> {
        switch (cardId) {
            case 'devices':
                return this.statsRepoService.getDevices(params);
            case 'operatingSystems':
                return this.statsRepoService.getOs(params);
            case 'browsers':
                return this.statsRepoService.getBrowsers(params);
            case 'screenResolutions':
                return this.statsRepoService.getResolutions(params);
            default:
                return [];
        }
    }

    private createDoughnutChartData (rows: Array<{ label: string; visits: number }>): ChartData<'doughnut'> {
        return {
            labels: rows.map((row) => row.label),
            datasets: [
                {
                    data: rows.map((row) => row.visits),
                    backgroundColor: DEFAULT_DOUGHNUT_COLORS,
                    borderColor: DEFAULT_DOUGHNUT_COLORS,
                },
            ],
        };
    }

    private createDoughnutChartOptions (): ChartOptions<'doughnut'> {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
            },
        };
    }

    private createBubbleChartData (rows: Array<{ label: string; visits: number }>): ChartData<'bubble'> {
        const maxVisits = rows.reduce((max, row) => Math.max(max, row.visits), 0);

        return {
            labels: rows.map((row) => row.label),
            datasets: rows.map((row, index) => {
                const [x, y] = this.parseResolutionLabel(row.label);

                return {
                    label: row.label,
                    data: [
                        {
                            x,
                            y,
                            r: this.calculateBubbleRadius(row.visits, maxVisits),
                        },
                    ],
                    backgroundColor: DEFAULT_BUBBLE_COLORS[index] || DEFAULT_BUBBLE_COLORS[0],
                    borderColor: DEFAULT_BUBBLE_COLORS[index] || DEFAULT_BUBBLE_COLORS[0],
                };
            }),
        };
    }

    private createBubbleChartOptions (): ChartOptions<'bubble'> {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                },
                y: {
                    beginAtZero: true,
                },
            },
        };
    }

    private parseResolutionLabel (label: string): [number, number] {
        const [width, height] = label.split('x');
        return [this.toInteger(width), this.toInteger(height)];
    }

    private calculateBubbleRadius (value: number, maxValue: number): number {
        if (value <= 0 || maxValue <= 0) {
            return 0.00001;
        }

        const maxRadius = 25;
        if (value >= maxValue) {
            return maxRadius;
        }

        return (value * maxRadius) / maxValue;
    }

    private async loadSourcesData (requestId: number): Promise<void> {
        const params = this.buildDateParams();
        const summaryPromise = this.statsRepoService.getSummary(params).catch(() => null);

        const seriesConfigs = this.config.sourceSeries || [];
        const seriesPromises = seriesConfigs.map((series) => this.loadSourceSeries(series.id, params));

        const [summary, ...seriesResponses] = await Promise.all([summaryPromise, ...seriesPromises]);

        if (requestId !== this.latestRequestId) {
            return;
        }

        this.sourcesSummary = summary;

        seriesConfigs.forEach((series, index) => {
            this.sourceRemoteData[series.id] = seriesResponses[index] || [];
        });

        this.buildSourceChartData();
        await Promise.all([
            this.loadSourceReferrerCards(params, summary),
            this.loadSourceKeywords(params),
        ]);
    }

    private async loadSourceSeries (
        seriesKey: SiteAnalyticsSummarySourceSeriesKey,
        params: RequestParamsType,
    ): Promise<StatisticsVisitor[]> {
        const response = await this.statsRepoService.getVisitors({ ...params, type: seriesKey }).catch(() => null);
        return this.extractVisitorSeries(response, seriesKey);
    }

    private extractVisitorSeries (
        response: unknown,
        sourceType: SiteAnalyticsSummarySourceSeriesKey,
    ): StatisticsVisitor[] {
        if (Array.isArray(response)) {
            return response as StatisticsVisitor[];
        }

        if (response && typeof response === 'object' && sourceType in response) {
            const series = (response as Record<string, unknown>)[sourceType];
            if (Array.isArray(series)) {
                return series as StatisticsVisitor[];
            }
        }

        return [];
    }

    private buildSourceChartData (): void {
        const seriesConfigs = this.config.sourceSeries || [];
        const isLineChart = this.selectedSourceMetric === 'avgVisitTime' || this.selectedSourceMetric === 'bounceRate';

        this.sourceChartType = isLineChart ? 'line' : 'bar';

        const uniqueDates = new Set<string>();
        seriesConfigs.forEach((series) => {
            this.sourceRemoteData[series.id].forEach((entry) => {
                if (entry.date) {
                    uniqueDates.add(entry.date);
                }
            });
        });

        const sortedDates = [...uniqueDates].sort((first, second) => {
            return moment(first).valueOf() - moment(second).valueOf();
        });

        const labels = sortedDates.map((date) => moment(date).format('ll'));

        const datasets = seriesConfigs.map((series) => {
            const entriesByDate = new Map<string, StatisticsVisitor>();
            this.sourceRemoteData[series.id].forEach((entry) => {
                if (entry.date) {
                    entriesByDate.set(entry.date, entry);
                }
            });

            const values = sortedDates.map((date) => this.getSourceSeriesMetricValue(entriesByDate.get(date) || null));

            if (isLineChart) {
                return {
                    type: 'line' as const,
                    label: this.translocoService.translate(series.labelKey),
                    data: values,
                    borderColor: series.color,
                    backgroundColor: series.color,
                    borderWidth: 2,
                    fill: false,
                    tension: 0,
                };
            }

            return {
                type: 'bar' as const,
                label: this.translocoService.translate(series.labelKey),
                data: values,
                borderColor: series.color,
                backgroundColor: series.color,
                borderWidth: 1,
                stack: 'traffic-source-stack',
            };
        });

        this.sourceChartData = {
            labels,
            datasets,
        };

        this.hasSourceChartData = sortedDates.length > 0;
        this.sourceChartOptions = this.createSourceChartOptions(isLineChart);
    }

    private createSourceChartOptions (isLineChart: boolean): ChartOptions<'bar' | 'line'> {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                },
                tooltip: {
                    callbacks: {
                        label: (tooltipItem): string => {
                            const label = tooltipItem.dataset.label || '';
                            const value = Number(tooltipItem.raw || 0);

                            if (this.selectedSourceMetric === 'avgVisitTime') {
                                return `${label}: ${this.formatDuration(value)}`;
                            }

                            if (this.selectedSourceMetric === 'bounceRate') {
                                return `${label}: ${this.formatPercentage(value)}`;
                            }

                            return `${label}: ${this.formatNumber(value)}`;
                        },
                    },
                },
            },
            scales: {
                x: {
                    stacked: !isLineChart,
                },
                y: {
                    stacked: !isLineChart,
                    beginAtZero: true,
                    ticks: {
                        callback: (value): string => {
                            const numericValue = Number(value || 0);
                            if (this.selectedSourceMetric === 'avgVisitTime') {
                                return this.formatDuration(numericValue);
                            }
                            if (this.selectedSourceMetric === 'bounceRate') {
                                return this.formatPercentage(numericValue);
                            }
                            return this.formatNumber(numericValue);
                        },
                    },
                },
            },
        };
    }

    private getSourceSeriesMetricValue (entry: StatisticsVisitor | null): number {
        if (!entry) {
            return 0;
        }

        switch (this.selectedSourceMetric) {
            case 'uniqueVisits':
                return this.toInteger(entry.uniq_visitors);
            case 'pageViews':
                return this.toInteger(entry.actions);
            case 'avgVisitTime':
                return this.toInteger(entry.avg_time);
            case 'bounceRate':
                return this.calculateBounceRate(entry);
            default:
                return this.toInteger(entry.visits);
        }
    }

    private calculateBounceRate (entry: StatisticsVisitor): number {
        const visits = this.toFloat(entry.visits);
        const bounceRate = this.toFloat(entry.bounce_rate);

        if (visits <= 0 || bounceRate <= 0) {
            return 0;
        }

        return Math.round((bounceRate * 100) / visits);
    }

    private async loadSourceReferrerCards (
        params: RequestParamsType,
        summary: StatisticsSummary | null,
    ): Promise<void> {
        const referrerConfigs = this.config.sourceReferrerCards || [];
        const referrerParams = {
            ...params,
            limit: 3,
        };

        const [externalDomains, searchEngines, socialNetworks] = await Promise.all([
            this.statsRepoService.getReferrers(referrerParams).catch(() => []),
            this.statsRepoService.getSearchEngines(referrerParams).catch(() => []),
            this.statsRepoService.getSocials(referrerParams).catch(() => []),
        ]);

        const totalVisits = Math.max(this.toFloat(summary?.visits), 0);

        this.sourceReferrerCards = referrerConfigs.map((config) => {
            const source = this.getReferrerDataById(config.id, {
                externalDomains,
                searchEngines,
                socialNetworks,
            });

            return {
                id: config.id,
                titleKey: config.titleKey,
                overviewLinkLabelKey: config.overviewLinkLabelKey,
                overviewState: config.overviewState,
                items: source.slice(0, 3).map((entry) => {
                    const visits = this.toInteger(entry.visits);

                    return {
                        label: String(entry.label || entry.url || '-'),
                        visits,
                        percentage: totalVisits > 0 ? (visits * 100) / totalVisits : 0,
                    };
                }),
            };
        });
    }

    private getReferrerDataById (
        id: SiteAnalyticsSummaryReferrerCardConfig['id'],
        data: {
            externalDomains: StatisticsVisitor[];
            searchEngines: StatisticsVisitor[];
            socialNetworks: StatisticsVisitor[];
        },
    ): StatisticsVisitor[] {
        switch (id) {
            case 'externalReferringDomains':
                return data.externalDomains;
            case 'searchEngines':
                return data.searchEngines;
            case 'socialNetworks':
                return data.socialNetworks;
            default:
                return [];
        }
    }

    private async loadSourceKeywords (params: RequestParamsType): Promise<void> {
        const response = await this.statsRepoService.getKeywords({
            ...params,
            limit: 5,
        }).catch((): StatisticsKeyword[] => []);

        this.sourceKeywords = response.map((keyword): StatisticsKeyword => {
            return {
                ...keyword,
                word: String(keyword.word || '').replace(/&#(\d+);/g, (match, decimalValue) => {
                    return String.fromCharCode(Number(decimalValue));
                }) as 'Keyword not defined',
            };
        });
    }

    private buildDateParams (): RequestParamsType {
        if (!this.hasCompleteDateRange(this.primaryDateRange)) {
            return {};
        }

        return {
            from: moment(this.primaryDateRange[0]).format(STATISTICS.DATE_FORMAT),
            to: moment(this.primaryDateRange[1]).format(STATISTICS.DATE_FORMAT),
        };
    }

    private parseDateRange (from: unknown, to: unknown): [Date, Date] | null {
        if (typeof from !== 'string' || typeof to !== 'string' || !from || !to) {
            return null;
        }

        const fromDate = moment(from, STATISTICS.DATE_FORMAT, true);
        const toDate = moment(to, STATISTICS.DATE_FORMAT, true);

        if (!fromDate.isValid() || !toDate.isValid()) {
            return null;
        }

        return [fromDate.toDate(), toDate.toDate()];
    }

    private hasCompleteDateRange (value: [Date | null, Date | null] | null): value is [Date, Date] {
        return Boolean(value && value[0] instanceof Date && value[1] instanceof Date);
    }

    private toDateParam (value: Date): string {
        return moment(value).format(STATISTICS.DATE_FORMAT);
    }

    private toInteger (value: unknown): number {
        return Math.round(this.toFloat(value));
    }

    private toFloat (value: unknown): number {
        const parsed = Number.parseFloat(String(value ?? 0));
        return Number.isNaN(parsed) ? 0 : parsed;
    }
}
